// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, useTheme } from '@mui/system';
import { Alert, Button, Divider, Grid, IconButton, List, ListItem, ListItemIcon, Paper, Snackbar, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { UpdateCustomBundleThunk } from 'Features/bundle/bundleThunk';
import { useAppDispatch } from 'Hooks';
import { dollarsPerFoot } from 'Utils/numberFormat';
import stylesDef from './styles';

interface CustomBundleItemProps {
  bundle: ICustomBundle;
  handleOpenDeleteDialog: (bundleId: string) => void;
}

// What the bundle is built on (AUT-6630): a catalog product, an AEC bundle, or an old utilities bundle (Legacy)
const parentLine = (parent: ICustomBundleParent): string => {
  if (parent.kind === 'product') {
    return `Parent product: ${parent.name}${parent.inactive ? ' · Parent product retired' : ''}`;
  }
  return `Parent bundle: ${parent.name}${parent.kind === 'legacyUtilities' ? ' · Legacy' : ''}`;
};

const CustomBundleItem = ({ bundle, handleOpenDeleteDialog }: CustomBundleItemProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const [showMore, setShowMore] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(bundle.name);
  const [showError, setShowError] = useState(false);
  const [failedResultMessage, setFailedResultMessage] = useState('');
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedPricePerFoot, setEditedPricePerFoot] = useState(String(bundle.pricePerFoot ?? 0));
  const listPrice = bundle.parent?.kind === 'product' && !bundle.pricePerFootAdminSet;

  const handleSave = async () => {
    const saveResult = await dispatch(UpdateCustomBundleThunk(bundle._id, editedName));
    if (saveResult.success) {
      setIsEditing(false);
    } else {
      setIsEditing(false);
      setEditedName(bundle.name);
      setShowError(true);
      setFailedResultMessage(saveResult.message);
    }
  };

  // Admin-set contracted price per foot ($/ft). Saving via UpdateCustomBundleThunk marks the bundle
  // admin-set server-side (AUT-6265); the row re-renders from the response, no reload.
  const handleSavePrice = async () => {
    const rate = Number(editedPricePerFoot);
    if (!Number.isFinite(rate) || rate < 0) {
      setIsEditingPrice(false);
      setEditedPricePerFoot(String(bundle.pricePerFoot ?? 0));
      setShowError(true);
      setFailedResultMessage('Price per foot must be a non-negative number');
      return;
    }
    const saveResult = await dispatch(UpdateCustomBundleThunk(bundle._id, bundle.name, rate));
    if (saveResult.success) {
      setIsEditingPrice(false);
    } else {
      setIsEditingPrice(false);
      setEditedPricePerFoot(String(bundle.pricePerFoot ?? 0));
      setShowError(true);
      setFailedResultMessage(saveResult.message);
    }
  };

  return (
    <Paper elevation={1} sx={styles.paper}>
      <Snackbar open={showError} autoHideDuration={5000} onClose={() => setShowError(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" sx={styles.error} color="error">{failedResultMessage}</Alert>
      </Snackbar>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Box sx={styles.content}>
            {isEditing ? (
              <Box sx={styles.flexCenter}>
                <TextField
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  size="small"
                  variant="standard"
                  sx={styles.textField}
                />
                <IconButton aria-label="Save bundle name" onClick={handleSave}>
                  <CheckIcon sx={styles.finishedIcon} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={styles.flexCenter}>
                <Typography variant="h4" sx={styles.bundleTitle}>
                  {`${bundle.name} ${bundle.includeAttribute ? '(W/ ATTRIBUTES)' : ''}`}
                </Typography>
                <IconButton aria-label="Rename bundle" onClick={() => setIsEditing(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {bundle.parent && <Typography variant="body1" sx={styles.bundleParent}>{parentLine(bundle.parent)}</Typography>}
            {
              bundle.createdAt && (
                <Typography variant="body1" sx={styles.bundleDate}>
                  Created on:
                  {' '}
                  {new Date(bundle.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
              )
            }
          </Box>
        </Grid>
        <Grid item xs={1}>
          {/* layers */}
          <Box sx={styles.contentList}>
            <Typography sx={styles.contentCounter}>{bundle.layers.length}</Typography>
            <Typography sx={styles.contentCounterText}>Layers</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          {/* deliverables */}
          <Box sx={styles.contentList}>
            <Typography sx={styles.contentCounter}>{bundle.deliverables.length}</Typography>
            <Typography sx={styles.contentCounterText}>Deliverables</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          {/* Price Per Foot: the contracted rate only AirWorks sets (AUT-6265). A bundle on a catalog product with no rate
              prices at the catalog's list price (AUT-6630 D3) */}
          <Box sx={styles.contentList}>
            {isEditingPrice ? (
              <Box sx={styles.flexCenter}>
                <TextField
                  value={editedPricePerFoot}
                  onChange={(e) => setEditedPricePerFoot(e.target.value)}
                  size="small"
                  variant="standard"
                  type="number"
                  inputProps={{ min: 0, step: 0.0001 }}
                  sx={styles.textField}
                />
                <IconButton aria-label="Save price per foot" onClick={handleSavePrice}>
                  <CheckIcon sx={styles.finishedIcon} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={styles.flexCenter}>
                <Typography sx={styles.contentCounter}>{listPrice ? 'List price' : dollarsPerFoot(bundle.pricePerFoot ?? 0)}</Typography>
                {_ADMIN_ && (
                  <IconButton
                    aria-label="Edit price per foot"
                    onClick={() => { setEditedPricePerFoot(String(bundle.pricePerFoot ?? 0)); setIsEditingPrice(true); }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
            <Typography sx={styles.contentCounterText}>Price Per Foot</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Button onClick={() => setShowMore(!showMore)}>{showMore ? 'HIDE LAYERS' : 'SHOW LAYERS'}</Button>
        </Grid>
        <Grid item xs={1}>
          <IconButton aria-label="Delete bundle" onClick={() => handleOpenDeleteDialog(bundle._id)}>
            <DeleteIcon sx={styles.deleteIcon} />
          </IconButton>
        </Grid>
      </Grid>

      {
        showMore && (
          <>
            <Divider sx={styles.divider} />
            <Box sx={styles.expandedContent}>
              <Box sx={styles.expandedColumn}>
                <Typography variant="h5">Layers</Typography>
                <List>
                  {
                    bundle.layers.map((layer: ICustomBundleAddon) => (
                      <ListItem sx={styles.bundleListItem} key={layer._id}>
                        <ListItemIcon sx={styles.listItemIcon}>
                          <CheckIcon sx={styles.finishedIcon} />
                        </ListItemIcon>
                        <Typography>
                          {layer.name}
                          {' '}
                          {layer.is3D ? '(3D)' : ''}
                        </Typography>
                      </ListItem>
                    ))
                  }
                </List>
              </Box>
              <Box sx={styles.expandedColumn}>
                <Typography variant="h5">Deliverables</Typography>
                <List>
                  {
                    bundle.deliverables.map((deliverable: ICustomBundleAddon) => (
                      <ListItem sx={styles.bundleListItem} key={deliverable._id}>
                        <ListItemIcon sx={styles.listItemIcon}>
                          <CheckIcon sx={styles.finishedIcon} />
                        </ListItemIcon>
                        <Typography>{deliverable.name}</Typography>
                      </ListItem>
                    ))
                  }
                </List>
              </Box>
            </Box>
          </>
        )
      }
    </Paper>
  );
};

export default CustomBundleItem;
